// AI Director (Phase 26) → studio prompt hand-off. The Director stashes the
// distilled creation brief here before navigating; the target studio consumes
// it exactly once on mount.
const KEY = "mist_director_prompt";

export function consumeDirectorPrompt() {
  try {
    const v = sessionStorage.getItem(KEY);
    if (v) sessionStorage.removeItem(KEY);
    return v || "";
  } catch {
    return "";
  }
}

export function setDirectorPrompt(prompt) {
  try {
    if (prompt) sessionStorage.setItem(KEY, prompt);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — navigation still works, just without prefill */
  }
}

// Stitching Studio → Mist Director asset hand-off. Stashes the freshly compiled
// reel ({asset_id, filename, kind}) so the Director can pre-load it for
// captioning/hashtags with no download/re-upload. Consumed once on Director mount.
const ASSET_KEY = "mist_director_asset";

export function consumeDirectorAsset() {
  try {
    const v = sessionStorage.getItem(ASSET_KEY);
    if (v) sessionStorage.removeItem(ASSET_KEY);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

export function setDirectorAsset(asset) {
  try {
    if (asset) sessionStorage.setItem(ASSET_KEY, JSON.stringify(asset));
    else sessionStorage.removeItem(ASSET_KEY);
  } catch {
    /* storage unavailable — navigation still works, just without the pre-load */
  }
}

// ---------------------------------------------------------------------------
// Director ⇆ Place Workspace round-trip (write-once / consume-once).
// OUTBOUND: Director stashes the active scene's context (scene_index, prompt,
// characters) before routing to Place. INBOUND: Place stashes the winning
// generated asset id to hand back so the Director can drop it onto that scene
// as its background plate / generation seed.
// ---------------------------------------------------------------------------
const KEYS = {
  OUTBOUND_CONTEXT: "mist_director_to_place_context",
  INBOUND_RESULT: "mist_place_to_director_result",
};
const CONTEXT_TTL_MS = 300000; // 5 min staleness guard

export function setDirectorContext(payload) {
  try {
    sessionStorage.setItem(KEYS.OUTBOUND_CONTEXT, JSON.stringify({ ...payload, ts: Date.now() }));
  } catch { /* storage unavailable — navigation still works */ }
}

export function getDirectorContext() {
  try {
    const data = sessionStorage.getItem(KEYS.OUTBOUND_CONTEXT);
    if (!data) return null;
    const parsed = JSON.parse(data);
    if (Date.now() - (parsed.ts || 0) > CONTEXT_TTL_MS) {
      sessionStorage.removeItem(KEYS.OUTBOUND_CONTEXT);
      return null;
    }
    return parsed;
  } catch { return null; }
}

// `ctx` override lets the caller pass a context it captured on mount, so a slow
// generation that outlives the 5-min TTL still returns to the right scene.
export function setPlaceResult(assetId, ctx = null) {
  try {
    const context = ctx || getDirectorContext() || {};
    sessionStorage.setItem(KEYS.INBOUND_RESULT, JSON.stringify({
      asset_id: assetId,
      project_id: context.project_id,
      scene_index: context.scene_index,
      ts: Date.now(),
    }));
  } catch { /* storage unavailable */ }
}

export function consumePlaceResult() {
  try {
    const data = sessionStorage.getItem(KEYS.INBOUND_RESULT);
    if (!data) return null;
    sessionStorage.removeItem(KEYS.INBOUND_RESULT);
    sessionStorage.removeItem(KEYS.OUTBOUND_CONTEXT); // clear the outbound leg too
    return JSON.parse(data);
  } catch { return null; }
}

// Director → Music Viz handover (write-once / consume-once).
export function setMusicVizPrefill(payload) {
  try {
    sessionStorage.setItem("mist_music_viz_prefill", JSON.stringify({ ...payload, ts: Date.now() }));
  } catch { /* storage unavailable */ }
}

export function consumeMusicVizPrefill() {
  try {
    const data = sessionStorage.getItem("mist_music_viz_prefill");
    if (!data) return null;
    sessionStorage.removeItem("mist_music_viz_prefill");
    const parsed = JSON.parse(data);
    if (Date.now() - (parsed.ts || 0) > 300000) return null; // 5-min staleness guard
    return parsed;
  } catch { return null; }
}

// ---------------------------------------------------------------------------
// Create-Song round-trip. A launching surface (Singer Studio Workspace 1 or
// Music Viz) stashes an ORIGIN token, opens the full Mist Music Studio, then
// gets the finished track handed back as a RESULT — so it's always crystal
// clear where the user came from and where the "Back / Use in <Project>"
// button should return to.
// ---------------------------------------------------------------------------
const SONG_ORIGIN_KEY = "mist_song_origin";
const SONG_RESULT_KEY = "mist_song_result";
const SONG_TTL_MS = 3600000; // 1h — song production can be slow

// origin: { returnUrl, label, projectName }. `getSongOrigin` is a peek (kept
// alive while the user is in the studio); clear it explicitly on return.
export function setSongOrigin(payload) {
  try { sessionStorage.setItem(SONG_ORIGIN_KEY, JSON.stringify({ ...payload, ts: Date.now() })); }
  catch { /* storage unavailable — navigation still works, just without the return label */ }
}
export function getSongOrigin() {
  try {
    const d = sessionStorage.getItem(SONG_ORIGIN_KEY);
    if (!d) return null;
    const p = JSON.parse(d);
    if (Date.now() - (p.ts || 0) > SONG_TTL_MS) { sessionStorage.removeItem(SONG_ORIGIN_KEY); return null; }
    return p;
  } catch { return null; }
}
export function clearSongOrigin() {
  try { sessionStorage.removeItem(SONG_ORIGIN_KEY); } catch { /* ignore */ }
}

// result: { assetId, title }. Consumed once by the surface on return.
export function setSongResult(result) {
  try { sessionStorage.setItem(SONG_RESULT_KEY, JSON.stringify({ ...result, ts: Date.now() })); }
  catch { /* storage unavailable */ }
}
export function consumeSongResult() {
  try {
    const d = sessionStorage.getItem(SONG_RESULT_KEY);
    if (!d) return null;
    sessionStorage.removeItem(SONG_RESULT_KEY);
    const p = JSON.parse(d);
    if (Date.now() - (p.ts || 0) > SONG_TTL_MS) return null;
    return p;
  } catch { return null; }
}

// ---------------------------------------------------------------------------
// Singer → Music Viz direct handoff (Audio-Decoupled pipeline, Step 3).
// Singer stashes the finished (ideally silent, lip-synced) video + master track
// and a skipSetup flag; Music Viz consumes it once on mount and drops the user
// straight into the align/styling canvas — zero setup clicks.
// ---------------------------------------------------------------------------
const SINGER_VIZ_KEY = "mist_singer_to_viz";
const SINGER_VIZ_TTL_MS = 600000; // 10 min

export function setSingerToViz(payload) {
  // { skipSetup, video_asset_id, song_asset_id, song_title, project_name }
  try { sessionStorage.setItem(SINGER_VIZ_KEY, JSON.stringify({ ...payload, ts: Date.now() })); }
  catch { /* storage unavailable */ }
}
export function consumeSingerToViz() {
  try {
    const d = sessionStorage.getItem(SINGER_VIZ_KEY);
    if (!d) return null;
    sessionStorage.removeItem(SINGER_VIZ_KEY);
    const p = JSON.parse(d);
    if (Date.now() - (p.ts || 0) > SINGER_VIZ_TTL_MS) return null;
    return p;
  } catch { return null; }
}
